import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import { IMPROVE_TEXT } from '@modules/_shared/common/queries';
import {
  AI_LANGUAGE_OPTIONS,
  AiDescriptionMenu,
} from '@modules/_shared/components/AiDescriptionMenu';
import { RaSelect } from '@modules/_shared/components/form/RaSelect';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';
import { type Language } from '@shared/LocaleContext';

const GENERATE_DESCRIPTION = gql(/* GraphQL */ `
  query LotGenerateDescription($lot_id: uuid!, $language: String) {
    generate_listings_description(lot_id: $lot_id, language: $language) {
      description
    }
  }
`);

type LotGenerateDescriptionProps = {
  lot_id: string | null | undefined;
  description: string;
  language?: string;
  onComplete: (description: string) => void;
  onError?: (error: ErrorLike) => void;
  translateFrom?: (sourceLanguage: Language) => Promise<void>;
  sourceLanguages?: { code: Language; name: string }[];
  hideExpertMode?: boolean;
};

type FormValues = {
  language: string;
};

export const LotGenerateDescription = ({
  lot_id,
  description,
  language,
  onComplete,
  onError,
  translateFrom,
  sourceLanguages,
  hideExpertMode,
}: LotGenerateDescriptionProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { t } = useLocale();
  const { control, watch } = useForm<FormValues>({
    defaultValues: { language: '' },
  });
  const expertLanguage = watch('language');

  const [generateDesc, { loading: loadingGenerate }] = useLazyQuery(
    GENERATE_DESCRIPTION,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [improveDesc, { loading: loadingImprove }] = useLazyQuery(
    IMPROVE_TEXT,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  const applyGeneratedDescription = (value: string | null | undefined) => {
    if (value) {
      onComplete(value);
    }
  };

  const reportError = (error: ErrorLike) => {
    if (error.name !== 'AbortError') {
      onError?.(error);
    }
  };

  const handleDialogSave = () => {
    setDialogOpen(false);
    generateDesc({
      variables: {
        lot_id: lot_id ?? '',
        language: expertLanguage || language || null,
      },
    })
      .then(({ data }) => {
        applyGeneratedDescription(
          data?.generate_listings_description?.description,
        );
      })
      .catch(reportError);
  };

  const isLoading = loadingGenerate || loadingImprove;

  return (
    <>
      <AiDescriptionMenu
        onGenerate={
          lot_id != null
            ? () => {
                generateDesc({
                  variables: {
                    lot_id,
                    language: expertLanguage || language || null,
                  },
                })
                  .then(({ data }) => {
                    applyGeneratedDescription(
                      data?.generate_listings_description?.description,
                    );
                  })
                  .catch(reportError);
              }
            : undefined
        }
        onImprove={() => {
          improveDesc({
            variables: {
              text: description,
            },
          })
            .then(({ data }) => {
              applyGeneratedDescription(data?.improve_text?.text);
            })
            .catch(reportError);
        }}
        onExpertMode={!hideExpertMode ? () => setDialogOpen(true) : undefined}
        isLoading={isLoading}
        hasDescription={!!description}
        translateFrom={translateFrom}
        sourceLanguages={sourceLanguages}
      />
      <SlideDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t('Expert mode')}
        onSave={handleDialogSave}
        saveText={t('ok')}
        saveIcon={null}
        maxWidth="xs"
      >
        <Box sx={{ p: 2 }}>
          <RaSelect
            name="language"
            label={t('Language')}
            control={control}
            options={AI_LANGUAGE_OPTIONS}
          />
        </Box>
      </SlideDialog>
    </>
  );
};
